import { createContext, createElement, useContext, useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { memberToken } from "@/lib/memberToken";
import { memberSessionProblem, memberProblem, purgeCacheStorage } from "@/lib/memberSession";

const MemberAuthContext = createContext(null);

// Upper limit for the login POST; no automatic retry.
const LOGIN_TIMEOUT_MS = 20000;

export function MemberAuthProvider({ children }) {
  // Memory-only auth: no persisted session. A fresh load => not authenticated,
  // false = not authenticated, object = authenticated.
  const [member, setMember] = useState(false);
  // Why the last session ended in this tab: null (none yet) | "logout" | "expired" | "invalid".
  const [ended, setEnded] = useState(null);
  // Monotonic auth-action generation: login, logout and every session end invalidate answers of older actions.
  const generation = useRef(0);

  // Taken before an await; afterwards the answer only counts if neither the generation nor the token session moved.
  const ticket = useCallback((bump) => {
    if (bump) generation.current += 1;
    const g = generation.current;
    const v = memberToken.getSessionVersion();
    return () => generation.current === g && memberToken.getSessionVersion() === v;
  }, []);

  // Any session end drops the member context at once; private views unmount, toasts and caches are cleared.
  useEffect(() => memberToken.onEnd((reason) => {
    setMember(false);
    setEnded(reason);
    toast.dismiss();
    purgeCacheStorage();
    generation.current += 1;
  }), []);

  const login = useCallback(async (email, password) => {
    const current = ticket(true);
    const { data } = await api.post("/member/auth/login", { email, password }, { timeout: LOGIN_TIMEOUT_MS });
    // A logout or newer login happened meanwhile: discard unused, the newer state stays untouched.
    if (!current()) return null;
    // A non-member session is discarded unused (never stored, never sent back).
    const problem = memberSessionProblem(data);
    if (problem) throw Object.assign(new Error(problem), { memberSessionProblem: problem });
    memberToken.set(data.access_token, data.refresh_token);
    setEnded(null);
    setMember(data.member);
    return data;
  }, [ticket]);

  const logout = useCallback(async () => {
    const current = ticket(true); // a still running login is invalid from here on
    try {
      await api.post("/member/auth/logout");
    } catch (_) {
      /* ignore */
    }
    if (current()) memberToken.end("logout");
  }, [ticket]);

  const refresh = useCallback(async () => {
    const current = ticket(false);
    const { data } = await api.get("/member/auth/me");
    if (!current()) return null;
    if (memberProblem(data?.member)) {
      memberToken.end("invalid");
      return false;
    }
    setMember(data.member);
    return data.member;
  }, [ticket]);

  return createElement(MemberAuthContext.Provider, { value: { member, ended, setMember, login, logout, refresh } }, children);
}

export function useMemberAuth() {
  const ctx = useContext(MemberAuthContext);
  if (!ctx) throw new Error("useMemberAuth must be used within MemberAuthProvider");
  return ctx;
}
